import { ReactNode } from 'react'
import dayjs from 'dayjs'
import { Text } from '@lmc/ui-kit/src/general'
import styled from 'styled-components'
import {
  AdministrationStatus,
  MarChartData,
  MedicationTableData,
  MedicationTableRow
} from '../types'
import Administered from '../iconsV2/Administered'
import Discontinued from '../iconsV2/Discontinued'
import Missed from '../iconsV2/Missed'
import Paused from '../iconsV2/Paused'
import NotScheduledOrNotNeeded from '../iconsV2/NotScheduledOrNotNeeded'

const StyledCode = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.color.neutral.color3};
`

export type AdministrationStatusDisplay = {
  display: ReactNode
  translationKey: string
}

export const getAdministrationStatusDisplay = (
  status: AdministrationStatus
): AdministrationStatusDisplay => {
  switch (status) {
    case AdministrationStatus.GIVEN:
      return {
        display: <Administered />,
        translationKey: 'ADMINISTERED'
      }
    case AdministrationStatus.VERBAL_PROMPT:
      return {
        display: <StyledCode level={2}>VP</StyledCode>,
        translationKey: 'VERBAL_PROMPT'
      }
    case AdministrationStatus.ABSENT:
      return {
        display: <StyledCode level={2}>A</StyledCode>,
        translationKey: 'ABSENT'
      }
    case AdministrationStatus.OTHER:
      return {
        display: <StyledCode level={2}>O</StyledCode>,
        translationKey: 'OTHER'
      }
    case AdministrationStatus.ALREADY_TAKEN:
      return {
        display: <StyledCode level={2}>AT</StyledCode>,
        translationKey: 'ALREADY_TAKEN'
      }
    case AdministrationStatus.NOT_NEEDED:
      return {
        display: <NotScheduledOrNotNeeded />,
        translationKey: 'NOT_SCHEDULED_OR_NOT_NEEDED'
      }
    case AdministrationStatus.LEFT_OUT:
      return {
        display: <StyledCode level={2}>LO</StyledCode>,
        translationKey: 'LEFT_OUT'
      }
    case AdministrationStatus.PAUSED:
      return {
        display: <Paused />,
        translationKey: 'PAUSED'
      }
    case AdministrationStatus.NAUSEA_VOMITING:
      return {
        display: <StyledCode level={2}>N/V</StyledCode>,
        translationKey: 'NAUSEA_OR_VOMITING'
      }
    case AdministrationStatus.DISCONTINUED:
      return {
        display: <Discontinued />,
        translationKey: 'DISCONTINUED'
      }
    case AdministrationStatus.REFUSED:
      return {
        display: <StyledCode level={2}>R</StyledCode>,
        translationKey: 'REFUSED'
      }
    case AdministrationStatus.MISSED:
      return {
        display: <Missed />,
        translationKey: 'MISSED'
      }
    case AdministrationStatus.REFUSED_DESTROYED:
      return {
        display: <StyledCode level={2}>RD</StyledCode>,
        translationKey: 'REFUSED_AND_DESTROYED'
      }
    default:
      return {
        display: <Administered />,
        translationKey: 'ADMINISTERED'
      }
  }
}

const formatDose = (dose: string | object): string => {
  if (typeof dose === 'string') {
    return dose
  }
  if (typeof dose === 'object' && dose !== null) {
    return JSON.stringify(dose)
  }
  return ''
}

const mapApiStatusToAdministrationStatus = (
  status: string
): AdministrationStatus | null => {
  const upperStatus = status.toUpperCase()
  const statusValues = Object.values(AdministrationStatus) as string[]

  if (statusValues.includes(upperStatus)) {
    return upperStatus as AdministrationStatus
  }

  return null
}

export const transformMarChartData = (
  data: MarChartData | null | undefined,
  type: 'scheduled' | 'prn',
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): MedicationTableRow[] => {
  if (!data || !data.medications) {
    return []
  }

  const rows: MedicationTableRow[] = []
  let rowIndex = 0

  const filteredMedications = data.medications.filter(
    (med) => med.prn === (type === 'prn')
  )

  filteredMedications.forEach((medication) => {
    medication.roundTimings.forEach((timing) => {
      const medicationData: MedicationTableData = {
        name: medication.medicationName,
        currentStock: medication.currentStock || 0,
        startDate: dayjs(medication.startDate).format('DD-MM-YYYY'),
        endDate: '',
        isControlledDrug: medication.controlledDrug,
        lowStock:
          (medication.currentStock || 0) > 0 &&
          (medication.currentStock || 0) <= 20,
        outOfStock: (medication.currentStock || 0) === 0,
        stockUnit: medication.stockUnit || medication.quantityType || 'tablet'
      }

      const row: MedicationTableRow = {
        key: `med-${medication.residentMedicationId}-${timing.roundTimingId}-${rowIndex}`,
        medication: medicationData,
        dose: formatDose(timing.dose),
        residentMedicationId: medication.residentMedicationId
      }

      if (type === 'scheduled') {
        row.hour = timing.timingTime
      }

      const dateMap: Record<string, any> = {}

      timing.dateStatuses.forEach((dateStatus) => {
        const dateKey = dayjs(dateStatus.date).format('DD-MM-YYYY')
        const administrationStatus = mapApiStatusToAdministrationStatus(
          dateStatus.status
        )

        if (
          !administrationStatus ||
          administrationStatus === AdministrationStatus.SCHEDULED
        ) {
          return
        }

        const statusDisplay =
          getAdministrationStatusDisplay(administrationStatus)

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            outcomes: []
          }
        }

        const outcome: any = {
          action: statusDisplay.translationKey,
          date: dateKey
        }

        if (
          administrationStatus === AdministrationStatus.GIVEN &&
          dateStatus.recordedBy
        ) {
          outcome.recordDetails = {
            quantity:
              dateStatus.doseQuantity?.toString() || formatDose(timing.dose),
            timeRecorded:
              dateStatus.administeredTime ||
              dayjs(dateStatus.date).toISOString(),
            recordedById: dateStatus.recordedBy.id,
            recordedBy: `${dateStatus.recordedBy.name.first} ${dateStatus.recordedBy.name.last}`,
            recordedByPicture: dateStatus.recordedBy.picture,
            comment: dateStatus.details,
            comments: dateStatus.details ? [dateStatus.details] : []
          }
        }

        dateMap[dateKey].outcomes.push(outcome)
      })

      Object.keys(dateMap).forEach((dateKey) => {
        row[dateKey] = dateMap[dateKey]
      })

      rows.push(row)
      rowIndex++
    })
  })

  return rows
}
